import { createContext, PropsWithChildren, useContext } from 'react';

import { ScopedBoolean } from '@/api';
import { ProductAttributesByArticle } from '@/models/productAttribute';
import { type RequestWithRelations } from '@/models/request';
import {
  getScopedBoolean as getWorkflowScopedBoolean,
  hasOnlinePaymentStep,
  WorkflowPublicWithRelations,
} from '@/models/workflow';
import { RequestContextData } from '@/routes/Requests/contexts/interfaces';
import { I18nWording } from '@/services/i18n';

type BrandRequestContextData = {
  request: RequestWithRelations;
  workflow: WorkflowPublicWithRelations | undefined;
  productAttributesData: ProductAttributesByArticle[];
  view: {
    client: RequestContextData['view']['client'];
    articles: {
      details: {
        color: {
          shown: boolean;
          required: boolean;
        };
        size: {
          shown: boolean;
          required: boolean;
        };
        articlePhoto: {
          shown: boolean;
          required: boolean;
        };
      };
      warranty: {
        shown: boolean;
        fields: {
          purchaseDate: {
            shown: boolean;
            required: boolean;
          };
          purchaseLocation: {
            shown: boolean;
            required: boolean;
          };
          purchaseCountry: {
            shown: boolean;
            required: boolean;
          };
          purchaseChannel: {
            shown: boolean;
            required: boolean;
            options: I18nWording[];
          };
          orderNumber: {
            shown: boolean;
            required: boolean;
          };
          proofOfPurchase: {
            shown: boolean;
            required: boolean;
          };
          proofOfPurchaseOrOrderNumber: {
            required: boolean;
          };
        };
      };
      defects: {
        customEnabled: boolean;
        photo: {
          shown: boolean;
          required: boolean;
        };
        context: {
          shown: boolean;
          required: boolean;
        };
        brandResponsibility: {
          shown: (article: { activeWarrantyType: string | null }) => boolean;
          exceptionalCoverage: {
            shown: boolean;
          };
        };
      };
    };
  };
};

const BrandRequestContext = createContext({} as BrandRequestContextData);

export const useBrandRequestContext = () => useContext(BrandRequestContext);

export const BrandRequestContextProvider = ({
  request,
  workflow,
  productAttributesData,
  children,
}: PropsWithChildren<{
  request: RequestWithRelations;
  workflow: WorkflowPublicWithRelations | undefined;
  productAttributesData: ProductAttributesByArticle[];
}>) => {
  const value = useComputedRequestContext({ request, workflow, productAttributesData });

  return <BrandRequestContext value={value}>{children}</BrandRequestContext>;
};

function useComputedRequestContext({
  request,
  workflow,
  productAttributesData,
}: {
  request: RequestWithRelations;
  workflow: WorkflowPublicWithRelations | undefined;
  productAttributesData: ProductAttributesByArticle[];
}) {
  const getScopedBoolean = (scopedBoolean: ScopedBoolean | undefined) => {
    return getWorkflowScopedBoolean({
      scopedBoolean,
      credentialsScopes: request.scopes,
    });
  };

  const creationStep = workflow?.nodes.find((node) => 'step' in node && node.step === 'creation');
  const isDropOffSignatureEnabled = creationStep?.config?.dropOffSignature?.enabled ?? false;

  const isPickupSignatureEnabled = !!workflow?.nodes.find(
    (node) =>
      'step' in node && node.step === 'transit' && node.config.clientPickUpSignature?.enabled
  );

  const fieldsConfig = workflow?.config.fields?.request;

  const warrantyType = fieldsConfig?.articles?.warranty?.type ?? 'warranty';

  const brandResponsibilityEnabled =
    !!request.client &&
    getScopedBoolean(fieldsConfig?.articles?.warranty?.enabled) &&
    warrantyType === 'warranty';

  const requestView = {
    client: {
      enabled: getScopedBoolean(fieldsConfig?.client?.enabled),
      required: getScopedBoolean(fieldsConfig?.client?.required),
      vip: {
        enabled: getScopedBoolean(fieldsConfig?.client?.vip?.enabled),
      },
      name: {
        editable: getScopedBoolean(fieldsConfig?.client?.name?.editable),
      },
      composedName: {
        enabled: getScopedBoolean(fieldsConfig?.client?.composedName?.enabled),
      },
      address: {
        enabled: getScopedBoolean(fieldsConfig?.client?.address?.enabled),
      },
      billingAddress: {
        enabled: !!workflow && hasOnlinePaymentStep(workflow),
      },
      dropoffSignature: {
        enabled: isDropOffSignatureEnabled,
      },
      pickupSignature: {
        enabled: isPickupSignatureEnabled,
      },
    },
    articles: {
      details: {
        color: {
          shown: getScopedBoolean(fieldsConfig?.articles?.details?.color?.enabled),
          required: getScopedBoolean(fieldsConfig?.articles?.details?.color?.required),
        },
        size: {
          shown: getScopedBoolean(fieldsConfig?.articles?.details?.size?.enabled),
          required: getScopedBoolean(fieldsConfig?.articles?.details?.size?.required),
        },
        articlePhoto: {
          shown: getScopedBoolean(fieldsConfig?.articles?.details?.photo?.enabled),
          required: getScopedBoolean(fieldsConfig?.articles?.details?.photo?.required),
        },
      },
      warranty: {
        shown:
          !!request.client &&
          getScopedBoolean(fieldsConfig?.articles?.warranty?.enabled) &&
          fieldsConfig?.articles?.warranty?.type !== 'authenticity',
        fields: {
          purchaseDate: {
            shown: getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseDate?.enabled),
            required: getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseDate?.required),
          },
          purchaseLocation: {
            shown: getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseLocation?.enabled),
            required: getScopedBoolean(
              fieldsConfig?.articles?.warranty?.purchaseLocation?.required
            ),
          },
          purchaseCountry: {
            shown: getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseCountry?.enabled),
            required: getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseCountry?.required),
          },
          purchaseChannel: {
            shown:
              getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseChannel?.enabled) &&
              !!fieldsConfig?.articles?.warranty?.purchaseChannel?.options.length,
            required: getScopedBoolean(fieldsConfig?.articles?.warranty?.purchaseChannel?.required),
            options: fieldsConfig?.articles?.warranty?.purchaseChannel?.options ?? [],
          },
          orderNumber: {
            shown: getScopedBoolean(fieldsConfig?.articles?.warranty?.orderNumber?.enabled),
            required: getScopedBoolean(fieldsConfig?.articles?.warranty?.orderNumber?.required),
          },
          proofOfPurchase: {
            shown: getScopedBoolean(fieldsConfig?.articles?.warranty?.proofOfPurchase?.enabled),
            required: getScopedBoolean(fieldsConfig?.articles?.warranty?.proofOfPurchase?.required),
          },
          proofOfPurchaseOrOrderNumber: {
            required: getScopedBoolean(
              fieldsConfig?.articles?.warranty?.proofOfPurchaseOrOrderNumber?.required
            ),
          },
        },
      },
      defects: {
        customEnabled: getScopedBoolean(fieldsConfig?.articles?.defects?.custom?.enabled),
        photo: {
          shown: getScopedBoolean(fieldsConfig?.articles?.defects?.photo?.enabled),
          required: getScopedBoolean(fieldsConfig?.articles?.defects?.photo?.required),
        },
        context: {
          shown: getScopedBoolean(fieldsConfig?.articles?.defects?.context?.enabled),
          required: getScopedBoolean(fieldsConfig?.articles?.defects?.context?.required),
        },
        brandResponsibility: {
          shown: (article: { activeWarrantyType: string | null }) => {
            return brandResponsibilityEnabled && !!article.activeWarrantyType;
          },
          // Exceptional coverage can also be granted when the article is not under warranty
          exceptionalCoverage: {
            shown: brandResponsibilityEnabled,
          },
        },
      },
    },
  };

  return {
    request,
    workflow,
    productAttributesData,
    view: requestView,
  };
}
